import ScriptMediator from '../ScriptEngine';
import { DynamicFormPartType, ScriptDescriptionType } from '../Types';
import { ScanConfigType } from 'src/Modules/Scan';
import BaseScript from '../ScriptEngine/ScriptTypes';
import { PathUtil } from '../ScriptEngine/Utils/PathUtil';

export type BaseViewModelParams = {
	elementDescriptor: DynamicFormPartType;
	parent: BaseViewModel | null;
	businessEntityPath: string;
	viewModelPath: string;
	formObject?: any;
	collectionIndex?: number;
};

class BaseViewModel {
	static mediator: ScriptMediator;
	static EntityPathToViewModel: {
		[path: string]: BaseViewModel;
	} = {};
	static ViewModelPathToViewModel: {
		[path: string]: BaseViewModel;
	} = {};

	static dataContext: any = {};
	static formContextData: any = {};
	static attachmentsWithSizes: Record<string, number> = {};
	static fileId = '';
	static componentId = '';
	static typename = '';
	static projectNameWithSuffix = '';
	static scanSettings: ScanConfigType;
	static isClientScripts: boolean;
	static formWasLoaded = false;
	static isReadOnly = false;

	viewModelPath: string;
	elementDescriptor: DynamicFormPartType;
	Parent: BaseViewModel | null = null;
	FormObject: any;

	Path: string;
	LocalPath: string;
	FullPath: string;
	ControlKey: string;
	Caption: string;
	DescriptorKey: string;

	ControlEnabling = true;
	IsMandatory: boolean;
	ControlVisibility = true;
	IsValid = true;
	Width = 12;
	Value: any;
	Tooltip?: string;

	showErrors = true;
	errorMessage = '';
	notValidClass = false;

	ValidationScripts: BaseScript[] = [];
	DisplayScript?: BaseScript;
	CalculationScript?: BaseScript;
	DefaultValueScript?: BaseScript;
	MaxDateScript?: BaseScript;
	MinDateScript?: BaseScript;
	DefaultValue?: string;

	constructor(params: BaseViewModelParams) {
		this.elementDescriptor = params.elementDescriptor;
		this.Parent = params.parent;
		this.FormObject =
			params.formObject ||
			(this.Parent ? this.Parent.FormObject : undefined);
		this.viewModelPath = params.viewModelPath;
		this.Path = params.businessEntityPath;
		this.IsMandatory = params.elementDescriptor.IsMandatory || false;
		this.LocalPath = params.elementDescriptor.LocalPath;
		this.ControlKey = params.elementDescriptor.ControlKey;
		this.Caption = params.elementDescriptor.Caption;
		this.DescriptorKey = params.elementDescriptor.DescriptorKey;
		this.Path = params.elementDescriptor.Path;
		const displayResolvedOnClient =
			!!BaseViewModel.mediator && BaseViewModel.isClientScripts !== false;
		this.ControlVisibility = !(
			!!params.elementDescriptor.DisplayScript && displayResolvedOnClient
		);
		this.Tooltip = params.elementDescriptor.Tooltip;

		if (
			!!params.elementDescriptor.Width &&
			Number.isInteger(params.elementDescriptor.Width)
		) {
			this.Width = Number(params.elementDescriptor.Width);
		}

		BaseViewModel.ViewModelPathToViewModel[params.viewModelPath] = this;

		this.FullPath = this.getFullPath(params.collectionIndex);
		if (this.FullPath !== '.') {
			BaseViewModel.EntityPathToViewModel[this.FullPath] = this;
		}

		this.registerScripts();
	}

	getViewModelByPath = (path: string): BaseViewModel => {
		const absolutePath = PathUtil.toAbsolutePath(path, this.FullPath);
		return BaseViewModel.EntityPathToViewModel[absolutePath];
	};

	getFullPath = (collectionIndex: number = 0): string => {
		if (!this.Parent) {
			return this.LocalPath;
		} else if (
			this.Parent.elementDescriptor.DescriptorKey ===
			'CollectionFormDescriptor'
		) {
			// для коллекции при создании элемента надо сформировать путь с учётом индекса
			return (
				(this.Parent.FullPath === '.' ? '' : this.Parent.FullPath) +
				`[${collectionIndex}]`
			);
		} else if (
			this.Parent.elementDescriptor.DescriptorKey ===
			'PageContainerControlDescriptor'
		) {
			return (
				(this.Parent.FullPath === '.'
					? ''
					: this.Parent.FullPath + '.') + this.LocalPath
			);
		} else {
			return (
				(this.Parent.FullPath === '.'
					? ''
					: this.Parent.FullPath + '.') + this.LocalPath
			);
		}
	};

	registerScripts = () => {
		if (!BaseViewModel.mediator) {
			return;
		}
		BaseViewModel.mediator
			.getScriptTypes()
			.forEach((scriptType: string) => {
				const scriptDescriptor = (this.elementDescriptor as any)[
					scriptType
				];
				if (scriptDescriptor) {
					(this as any)[scriptType] =
						BaseViewModel.mediator.createScriptObject(
							this,
							scriptDescriptor as ScriptDescriptionType,
							scriptType,
						);
				}
			});
		// Создаём и регистрируем скрипты валидации
		if (this.elementDescriptor.ValidationScripts) {
			const validationScripts = [];

			for (
				let index = 0;
				index < this.elementDescriptor.ValidationScripts.length;
				index++
			) {
				validationScripts.push(
					BaseViewModel.mediator.createScriptObject(
						this,
						this.elementDescriptor.ValidationScripts[index],
						'ValidationScript',
					),
				);
			}
			this.ValidationScripts = validationScripts;
		}
	};

	/**  */
	getRootElement = () => {
		let element: BaseViewModel = this;
		while (element.Parent) {
			element = element.Parent;
		}
		return element;
	};

	/**  */
	getValue = () => {
		return this.Value ? this.Value : '';
	};

	/**  */
	setValue = (value: any) => {
		this.Value = value;
	};

	/**  */
	Validate = () => {
		const isVisible = this.IsControlVisible();
		let isValidated = false;
		if (isVisible) {
			const isMandatoryValid = this.ValidateMandatory();
			if (!isMandatoryValid) {
				isValidated = isMandatoryValid;
				this.errorMessage = 'Поле заполнено некорректно!';
			} else {
				isValidated =
					this.runValidation() && this.runValidationScripts();
			}
		}

		const valid = !isVisible || isValidated;
		this.IsValid = valid;
		if (valid === false) {
			if (this.notValidClass === false) {
				this.notValidClass = true;
			} else {
				this.notValidClass = false;
				this.notValidClass = true;
			}
		} else {
			this.notValidClass = false;
		}

		return valid;
	};

	getStringValue = (): string => {
		return this.Value ? this.Value.toString() : '';
	};

	/**
	 * Виден ли контрол с учётом видимости родителей
	 */
	IsControlVisible = (): boolean => {
		if (this.Parent) {
			return this.ControlVisibility && this.Parent.IsControlVisible();
		} else if (this.ControlVisibility !== undefined) {
			return this.ControlVisibility;
		} else {
			return true;
		}
	};

	ValidateMandatory = (): boolean => {
		const valid =
			!this.IsMandatory ||
			(this.getValue() !== undefined &&
				this.getValue() !== null &&
				this.getValue() !== '');
		this.IsValid = valid;
		return valid;
	};

	runValidation = (): boolean => {
		return true;
	};

	runValidationScripts = () => {
		this.errorMessage = '';
		if (this.ValidationScripts.length) {
			for (let i = 0; i < this.ValidationScripts.length; i++) {
				const script = this.ValidationScripts[i];
				if (script.canLaunchScript() && !script.runScript()) {
					this.errorMessage = this.ValidationScripts[i].Message ?? '';
					return false;
				}
			}
		}
		return true;
	};

	/**  */
	nullateProperties = (objectToClean: any, startNameTerm: string) => {
		for (const x in objectToClean) {
			if (
				objectToClean.hasOwnProperty(x) &&
				x.lastIndexOf(startNameTerm, 0) === 0
			) {
				objectToClean[x] = null;
			}
		}
	};

	getSimpleFormatXmlTagName = () => {
		return 'Text';
	};

	getSimpleFormatXmlOpen = (id?: string) => {
		let xml =
			'<' +
			this.getSimpleFormatXmlTagName() +
			' idRef="' +
			this.elementDescriptor.LocalPath +
			'" title="' +
			this.elementDescriptor.Caption +
			'" ';
		if (this.IsMandatory) {
			xml += 'isRequired="true"';
		}
		return xml + '>';
	};

	getSimpleFormatXmlClose = () => {
		return '</' + this.getSimpleFormatXmlTagName() + '>';
	};

	/**
	 * Определяет, должен ли контрол отображаться в текущем состоянии формы.
	 * Базовый метод всегда возвращает `true`.
	 * Может быть переопределён для реализации кастомной логики.
	 */
	isVisible = (readOnly: boolean): boolean => {
		return true;
	};
}

export default BaseViewModel;
